/**
 * SuperAdminMaintenancePanel — Outils de nettoyage de données héritées.
 *
 * Deux actions critiques pour la migration vers la politique mai 2026 :
 *   1. Nettoyer les emails fictifs `@noemail.local` / `@placeholder.local`
 *      (l'email est désormais optionnel — les anciens comptes en avaient un faux).
 *      → POST /api/superadmin/maintenance/cleanup-fake-emails
 *
 *   2. Recalculer les flags `isProxyEvaluation` (mode proxy retiré en mai 2026).
 *      → POST /api/performance-eval/admin/recompute-proxy-flags
 *
 * Chaque action propose un mode "Aperçu" (dry-run) puis une exécution réelle.
 */
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Mail, ShieldCheck, AlertTriangle, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

function ResultBlock({ data }) {
  if (!data) return null;
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
      <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

export default function SuperAdminMaintenancePanel() {
  const [emailsLoading, setEmailsLoading] = useState(null); // 'dry' | 'apply' | null
  const [emailsResult, setEmailsResult] = useState(null);
  const [proxyLoading, setProxyLoading] = useState(false);
  const [proxyResult, setProxyResult] = useState(null);

  const runCleanupFakeEmails = async (dryRun) => {
    if (!dryRun) {
      const confirmed = window.confirm(
        "Confirmer le nettoyage RÉEL des emails fictifs ?\n\n" +
        "Cette action retire le champ `email` de tous les utilisateurs / employés " +
        "dont l'adresse se termine par @noemail.local, @no-email.local ou @placeholder.local. " +
        "Les utilisateurs sans téléphone/matricule ne seront PAS modifiés (pour éviter de perdre leur connexion).\n\n" +
        "Action irréversible. Continuer ?"
      );
      if (!confirmed) return;
    }
    try {
      setEmailsLoading(dryRun ? 'dry' : 'apply');
      const { data } = await api.post('/superadmin/maintenance/cleanup-fake-emails', {
        domains: ['noemail.local', 'no-email.local', 'placeholder.local'],
        dry_run: dryRun,
      });
      setEmailsResult(data);
      toast.success(data?.message || (dryRun ? 'Aperçu terminé' : 'Nettoyage exécuté'));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur inconnue';
      toast.error(`Échec : ${msg}`);
    } finally {
      setEmailsLoading(null);
    }
  };

  const runRecomputeProxyFlags = async () => {
    const confirmed = window.confirm(
      "Confirmer le recalcul des flags `isProxyEvaluation` ?\n\n" +
      "Cette action force `isProxyEvaluation = false` sur TOUTES les évaluations " +
      "de votre entreprise (le mode proxy est retiré depuis mai 2026).\n\n" +
      "Continuer ?"
    );
    if (!confirmed) return;
    try {
      setProxyLoading(true);
      const { data } = await api.post('/performance-eval/admin/recompute-proxy-flags');
      setProxyResult(data);
      toast.success(data?.message || 'Flags recalculés');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur inconnue';
      toast.error(`Échec : ${msg}`);
    } finally {
      setProxyLoading(false);
    }
  };

  return (
    <Card data-testid="superadmin-maintenance-panel" className="border-amber-200 bg-amber-50/40">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <Wrench className="h-5 w-5" />
              Maintenance & Nettoyage
              <Badge variant="outline" className="ml-1 border-amber-300 bg-amber-100 text-amber-800">
                SuperAdmin
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1 text-amber-800/80">
              Outils de migration vers la politique mai 2026 (email optionnel, mode proxy retiré).
              À exécuter une seule fois en production après redéploiement.
            </CardDescription>
          </div>
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* 1. Nettoyer les emails fictifs */}
        <div className="rounded-lg border border-amber-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-amber-100 text-amber-700">
              <Mail className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-slate-900">
                1. Nettoyer les emails fictifs <code className="rounded bg-slate-100 px-1 text-[11px]">@noemail.local</code>
              </h4>
              <p className="mt-1 text-xs text-slate-600">
                Retire l'email fictif des anciens comptes créés sans adresse. Conserve <b>téléphone</b> et
                <b> matricule</b> comme identifiants. Les utilisateurs sans autre identifiant sont protégés.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={Boolean(emailsLoading)}
                  onClick={() => runCleanupFakeEmails(true)}
                  data-testid="maintenance-emails-dry"
                  className="border-amber-300 text-amber-800 hover:bg-amber-100"
                >
                  {emailsLoading === 'dry' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                  Aperçu (dry-run)
                </Button>
                <Button
                  size="sm"
                  disabled={Boolean(emailsLoading)}
                  onClick={() => runCleanupFakeEmails(false)}
                  data-testid="maintenance-emails-apply"
                  className="bg-amber-600 text-white hover:bg-amber-700"
                >
                  {emailsLoading === 'apply' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                  Exécuter le nettoyage
                </Button>
              </div>
              <ResultBlock data={emailsResult} />
            </div>
          </div>
        </div>

        {/* 2. Recalcul des flags proxy */}
        <div className="rounded-lg border border-amber-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-amber-100 text-amber-700">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-slate-900">
                2. Recalculer les flags <code className="rounded bg-slate-100 px-1 text-[11px]">isProxyEvaluation</code>
              </h4>
              <p className="mt-1 text-xs text-slate-600">
                Force <code className="rounded bg-slate-100 px-1 text-[11px]">isProxyEvaluation = false</code> sur
                toutes les évaluations de l'entreprise. Le mode proxy (Manager qui auto-évalue à la place
                d'un employé sans compte) est désactivé depuis mai 2026.
              </p>
              <div className="mt-3">
                <Button
                  size="sm"
                  disabled={proxyLoading}
                  onClick={runRecomputeProxyFlags}
                  data-testid="maintenance-proxy-recompute"
                  className="bg-amber-600 text-white hover:bg-amber-700"
                >
                  {proxyLoading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                  Recalculer les flags
                </Button>
              </div>
              <ResultBlock data={proxyResult} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
